import styled from '@grebban/style-system-react';
import loadable from '@loadable/component';
import PageMeta from '@fogia/core-web/components/metadata/PageMeta';
import Events, { EVENTS } from '@fogia/core-web/config/events';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
// Workaround, GTM events are registered on the core-web events package, not @grebban/events
import AppEvents from '@fogia/core-web/libs/Events';
import { PageEvents, ProductEvents } from '@fogia/core-web/libs/Events/constants';
import { debugHelper } from '@fogia/core-web/utils/debugHelper';
import ContentWrapper from 'components/ContentWrapper';
import React, { useEffect, useMemo } from 'react';

const Main = styled('main')``;

const StyledContentWrapper = styled(ContentWrapper)<{ customStyling: boolean }>`
    margin-top: ${({ customStyling }) =>
        customStyling ? undefined : 'calc(var(--header-height) + var(--banner-height))'};
    max-width: ${({ customStyling }) => (customStyling ? undefined : 'var(--site-corridor-max-width)')};
`;

export const viewsMapping = {
    contentPage: 'Views/Page',
    not_found: 'Views/NotFound',
    product: 'Views/Product',
    categoryPage: 'Views/Category',
    searchPage: 'Views/Search',
} as const;

const viewsWithoutMarginTop = new Set([]);

export type ViewsMapping = typeof viewsMapping;
export type ViewTypes = keyof ViewsMapping;
export type ViewTypeTemplates<ViewType extends ViewTypes> = keyof ViewsMapping[ViewType];
export type ViewComponentPath<
    ViewType extends ViewTypes,
    ViewTemplate extends keyof ViewsMapping[ViewType],
> = ViewsMapping[ViewType][ViewTemplate];

export interface ViewProps {
    id?: string;
    loading?: boolean;
    type: string;
}
const View: React.FunctionComponent<ViewProps> = ({ type, loading = false, id = '' }) => {
    // Map towards available view components
    const viewPath = viewsMapping[type] || viewsMapping?.not_found;
    const ViewComponent = useMemo(() => loadable(() => import(`${viewPath}`)), [viewPath]);

    const pageData = useAppSelector((state) => state.page.data);
    const applicationId = useAppSelector((state) => state.application.site.applicationId);

    debugHelper({
        context: 'View',
        message: 'Render:init',
        additionalParameters: { loading, type },
    });

    // Trigger page view based events on page id change
    useEffect(() => {
        Events.trigger(EVENTS.PAGE.VIEW);
        if (type === 'product') {
            Events.trigger(EVENTS.ECOMMERCE.PRODUCT.VIEW);
        }

        try {
            if (type) {
                setTimeout(() => {
                    AppEvents.trigger(PageEvents.VIEW, {
                        title: pageData!.title || pageData!.defaultName,
                        type,
                        applicationId,
                    });

                    if (type === 'product') {
                        AppEvents.trigger(ProductEvents.VIEW, { data: pageData!.product });
                    }
                });
            }
        } catch (e) {
            //
        }
    }, [id]);

    const customContentWrapperStyling = viewsWithoutMarginTop.has(viewPath as never);

    return (
        <Main>
            <StyledContentWrapper customStyling={customContentWrapperStyling}>
                <PageMeta />
                <ViewComponent opacity={loading ? '0' : '1'} />
            </StyledContentWrapper>
        </Main>
    );
};

export default View;
